import React, { useState, useEffect } from 'react'
import { X, Search, RotateCcw, AlertTriangle, Loader2, Ban } from 'lucide-react'
import { Transaction, RefundItem, PaymentMethod } from '@/types/pos'
import { apiService } from '@/services/api'
import { posApiService } from '@/services/posApiService'
import { useNotificationActions } from '@/contexts/NotificationContext'

interface RefundModalProps {
  onClose: () => void
  onRefundComplete: (refund: any) => void
  onVoidComplete?: () => void
}

function isSameDay(date: Date | string): boolean {
  const d = new Date(date)
  const today = new Date()
  return d.toDateString() === today.toDateString()
}

export const RefundModal: React.FC<RefundModalProps> = ({
  onClose,
  onRefundComplete,
  onVoidComplete,
}) => {
  const { showSuccess, showError } = useNotificationActions()
  const [searchTerm, setSearchTerm] = useState('')
  const [transactions, setTransactions] = useState<Transaction[]>([])
  const [selectedTransaction, setSelectedTransaction] = useState<Transaction | null>(null)
  const [refundItems, setRefundItems] = useState<Map<string, RefundItem>>(new Map())
  const [refundReason, setRefundReason] = useState('')
  const [refundMethod, setRefundMethod] = useState<Exclude<PaymentMethod, 'split'>>('cash')
  const [isLoading, setIsLoading] = useState(false)
  const [isProcessing, setIsProcessing] = useState(false)
  const [step, setStep] = useState<'search' | 'select-items' | 'confirm'>('search')
  const [transactionToVoid, setTransactionToVoid] = useState<Transaction | null>(null)
  const [voidReason, setVoidReason] = useState('')
  const [isVoiding, setIsVoiding] = useState(false)

  // Fetch recent transactions
  useEffect(() => {
    const fetchTransactions = async () => {
      setIsLoading(true)
      try {
        const response = await apiService.get<{ success: boolean; data: Transaction[] }>('/transactions')
        if (response.success) {
          const eligible = (response.data as Transaction[]).filter(
            t => t.status !== 'voided' && t.status !== 'refunded'
          )
          setTransactions(eligible)
        }
      } catch (error) {
        console.error('Failed to fetch transactions:', error)
      } finally {
        setIsLoading(false)
      }
    }
    fetchTransactions()
  }, [])

  const filteredTransactions = transactions.filter(t => {
    const term = searchTerm.toLowerCase()
    const transNum = (t.transactionNumber || t.receiptNumber || t.id || '').toLowerCase()
    const custName = ((t as any).customerName || '').toLowerCase()
    return transNum.includes(term) || custName.includes(term)
  })

  const canVoid = (t: Transaction) =>
    isSameDay(t.createdAt) && t.status !== 'voided' && t.status !== 'refunded' && t.status !== 'partial_refund'

  const handleVoidClick = (e: React.MouseEvent, transaction: Transaction) => {
    e.stopPropagation()
    setTransactionToVoid(transaction)
    setVoidReason('')
  }

  const handleVoidConfirm = async () => {
    if (!transactionToVoid || !voidReason.trim()) return
    setIsVoiding(true)
    try {
      const res = await posApiService.voidTransaction(transactionToVoid.id, voidReason.trim())
      if (res.success) {
        setTransactions(prev => prev.filter(t => t.id !== transactionToVoid.id))
        setTransactionToVoid(null)
        setVoidReason('')
        showSuccess('Transaction voided', `#${transactionToVoid.transactionNumber || transactionToVoid.id} has been voided.`)
        onVoidComplete?.()
      } else {
        showError('Void failed', (res as any).message || 'Could not void transaction.')
      }
    } catch (err: any) {
      showError('Void failed', err?.response?.data?.message || 'Could not void transaction.')
    } finally {
      setIsVoiding(false)
    }
  }

  const handleSelectTransaction = (transaction: Transaction) => {
    setSelectedTransaction(transaction)
    setRefundItems(new Map())
    setStep('select-items')
  }

  /** Get the max refundable quantity for an item (original qty minus already refunded) */
  const getMaxRefundableQty = (item: any): number => {
    const productId = item.productId || item.product?.id || item.sku
    const itemKey = item.batchId ? `${productId}-${item.batchId}` : productId
    const refundedItems = (selectedTransaction as any)?.refundedItems || {}
    const alreadyRefunded = Number(refundedItems[itemKey]) || 0
    return Math.max(0, (Number(item.quantity) || 0) - alreadyRefunded)
  }

  const toggleRefundItem = (item: any, checked: boolean, quantity?: number) => {
    const newRefundItems = new Map(refundItems)
    // Use batchId as part of the key to handle same product in different batches
    const itemId = item.batchId
      ? `${item.productId || item.product?.id || item.sku}-${item.batchId}`
      : (item.productId || item.product?.id || item.sku)

    if (checked) {
      const maxQty = getMaxRefundableQty(item)
      const qty = Math.min(quantity || maxQty, maxQty)
      if (qty <= 0) return // Nothing left to refund
      const itemQty = Number(item.quantity) || 1 // Guard against division by zero
      const itemTotal = Number(item.total || item.unitPrice * itemQty || item.product?.price * itemQty || 0) || 0
      const unitPrice = itemQty > 0 ? itemTotal / itemQty : 0

      newRefundItems.set(itemId, {
        productId: item.productId || item.product?.id || item.sku,
        productName: item.productName || item.product?.name || item.name,
        quantity: qty,
        refundAmount: unitPrice * qty,
        batchId: item.batchId || undefined,
        batchLabel: item.batchLabel || undefined,
      })
    } else {
      newRefundItems.delete(itemId)
    }

    setRefundItems(newRefundItems)
  }

  const updateRefundQuantity = (itemId: string, quantity: number, maxQty: number, unitPrice: number) => {
    const newRefundItems = new Map(refundItems)
    const existing = newRefundItems.get(itemId)
    
    if (existing && quantity > 0 && quantity <= maxQty) {
      newRefundItems.set(itemId, {
        ...existing,
        quantity,
        refundAmount: unitPrice * quantity,
      })
      setRefundItems(newRefundItems)
    }
  }

  const calculateRefundTotal = () => {
    let total = 0
    refundItems.forEach(item => {
      total += item.refundAmount
    })
    return total
  }

  const handleProcessRefund = async () => {
    if (!selectedTransaction || refundItems.size === 0 || !refundReason.trim()) return

    // Validate refund doesn't exceed original transaction
    const refundTotal = calculateRefundTotal()
    if (refundTotal > Number(selectedTransaction.total)) {
      showError('Invalid', `Refund amount (Rs.${refundTotal.toFixed(2)}) exceeds original transaction (Rs.${Number(selectedTransaction.total).toFixed(2)})`)
      return
    }

    setIsProcessing(true)
    try {
      const refundItemsArray = Array.from(refundItems.values()).map(item => ({
        productId: item.productId || null,
        productName: item.productName || null,
        quantity: item.quantity,
        total: item.refundAmount,
        batchId: item.batchId || null,
        batchLabel: item.batchLabel || null,
      }))

      // Call the backend refund endpoint
      const response = await apiService.post<{ success: boolean; data: any; message?: string }>(
        `/transactions/${selectedTransaction.id}/refund`,
        {
          items: refundItemsArray,
          reason: refundReason,
          refundAmount: calculateRefundTotal(),
          refundMethod,
        }
      )

      if (response.success) {
        const refundResult = {
          id: response.data?.id || `REF-${Date.now()}`,
          refundNumber: response.data?.transactionNumber || `REF-${Date.now()}`,
          originalTransactionId: selectedTransaction.id,
          originalTransactionNumber: selectedTransaction.transactionNumber || selectedTransaction.receiptNumber,
          items: refundItemsArray,
          total: calculateRefundTotal(),
          refundMethod,
          reason: refundReason,
          status: 'completed',
          createdAt: new Date(),
          ...response.data,
        }

        onRefundComplete(refundResult)
      }
    } catch (error: any) {
      console.error('Refund processing failed:', error)
      showError('Refund Failed', error?.response?.data?.message || 'Failed to process refund. Please try again.')
    } finally {
      setIsProcessing(false)
    }
  }

  const refundMethods = [
    { id: 'cash', label: 'Cash', icon: '💵' },
    { id: 'card', label: 'Card', icon: '💳' },
    { id: 'wallet', label: 'E-Wallet', icon: '📱' },
  ]

  const refundReasons = [
    'Customer changed mind',
    'Product defective/damaged',
    'Wrong item purchased',
    'Price match/adjustment',
    'Other',
  ]

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="glass-card max-w-2xl w-full max-h-[85vh] overflow-hidden flex flex-col relative">
        {/* Header */}
        <div className="flex justify-between items-center mb-6 flex-shrink-0">
          <div>
            <h3 className="text-xl font-bold esales-text flex items-center gap-2">
              <RotateCcw className="w-5 h-5" />
              Process Refund
            </h3>
            <p className="text-xs text-muted-foreground mt-1">
              {step === 'search' && 'Search for the original transaction'}
              {step === 'select-items' && 'Select items to refund'}
              {step === 'confirm' && 'Confirm refund details'}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-muted transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {/* Step 1: Search Transaction */}
          {step === 'search' && (
            <div className="space-y-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search by receipt/transaction number..."
                  className="w-full pl-10 pr-4 py-3 bg-background/50 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:outline-none"
                  autoFocus
                />
              </div>

              {isLoading ? (
                <div className="py-12 text-center">
                  <Loader2 className="w-8 h-8 animate-spin text-primary mx-auto mb-2" />
                  <p className="text-sm text-muted-foreground">Loading transactions...</p>
                </div>
              ) : (
                <div className="space-y-2 max-h-[400px] overflow-y-auto">
                  {filteredTransactions.length === 0 ? (
                    <div className="py-12 text-center text-muted-foreground">
                      <p>No transactions found</p>
                    </div>
                  ) : (
                    filteredTransactions.slice(0, 20).map((transaction) => (
                      <div
                        key={transaction.id}
                        onClick={() => handleSelectTransaction(transaction)}
                        className="p-4 rounded-xl border border-border/50 hover:border-primary/50 cursor-pointer transition-all bg-muted/10 hover:bg-muted/20"
                      >
                        <div className="flex justify-between items-start gap-2">
                          <div className="flex-1 min-w-0">
                            <p className="font-bold text-sm">
                              #{transaction.transactionNumber || transaction.receiptNumber || transaction.id}
                            </p>
                            <p className="text-xs text-muted-foreground mt-1">
                              {new Date(transaction.createdAt).toLocaleString()}
                            </p>
                          </div>
                          <div className="text-right flex-shrink-0">
                            <p className="font-bold text-primary">Rs.{Number(transaction.total).toFixed(2)}</p>
                            <p className="text-xs text-muted-foreground">
                              {(transaction.items as any[]).length} item(s)
                            </p>
                            {transaction.status === 'partial_refund' && (
                              <span className="inline-block mt-1 text-[9px] font-bold uppercase px-1.5 py-0.5 rounded bg-yellow-500/15 text-yellow-500 leading-none">
                                Partially refunded
                              </span>
                            )}
                            {canVoid(transaction) && (
                              <button
                                type="button"
                                onClick={(e) => handleVoidClick(e, transaction)}
                                className="mt-2 px-2 py-1 text-xs font-medium rounded-lg bg-red-500/15 text-red-600 dark:text-red-400 hover:bg-red-500/25 border border-red-500/30"
                              >
                                Void
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          )}

          {/* Step 2: Select Items */}
          {step === 'select-items' && selectedTransaction && (
            <div className="space-y-4">
              <div className="p-3 bg-muted/20 rounded-lg">
                <p className="text-xs text-muted-foreground">Original Transaction</p>
                <p className="font-bold">
                  #{selectedTransaction.transactionNumber || selectedTransaction.receiptNumber}
                </p>
                <p className="text-xs text-muted-foreground">
                  {new Date(selectedTransaction.createdAt).toLocaleString()} • Total: Rs.{selectedTransaction.total.toFixed(2)}
                </p>
              </div>

              <div className="space-y-2">
                <p className="text-sm font-semibold">Select items to refund:</p>
                {(selectedTransaction.items as any[]).map((item, index) => {
                  const itemId = item.batchId
                    ? `${item.productId || item.product?.id || item.sku}-${item.batchId}`
                    : (item.productId || item.product?.id || item.sku || index.toString())
                  const isSelected = refundItems.has(itemId)
                  const selectedItem = refundItems.get(itemId)
                  const itemQty = Number(item.quantity) || 1
                  const itemTotal = Number(item.total || item.unitPrice * itemQty || item.product?.price * itemQty || 0) || 0
                  const unitPrice = itemQty > 0 ? itemTotal / itemQty : 0
                  const maxRefundable = getMaxRefundableQty(item)
                  const alreadyRefundedQty = itemQty - maxRefundable
                  const isFullyRefunded = maxRefundable <= 0

                  return (
                    <div
                      key={`${index}-${item.batchId || 'default'}`}
                      className={`p-3 rounded-lg border-2 transition-all ${
                        isFullyRefunded ? 'border-border/30 bg-muted/5 opacity-50' :
                        isSelected ? 'border-primary bg-primary/5' : 'border-border/50 bg-muted/10'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={(e) => toggleRefundItem(item, e.target.checked)}
                          disabled={isFullyRefunded}
                          className="w-4 h-4 rounded border-border accent-primary"
                        />
                        <div className="flex-1">
                          <p className="font-semibold text-sm">
                            {item.productName || item.product?.name || item.name}
                          </p>
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="text-xs text-muted-foreground">
                              Rs.{unitPrice.toFixed(2)} × {item.quantity} = Rs.{itemTotal.toFixed(2)}
                            </p>
                            {item.batchId && (
                              <span className="text-[9px] font-bold uppercase px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-400 leading-none">
                                Batch
                              </span>
                            )}
                            {alreadyRefundedQty > 0 && (
                              <span className="text-[9px] font-bold uppercase px-1.5 py-0.5 rounded bg-red-500/15 text-red-400 leading-none">
                                {isFullyRefunded ? 'Fully refunded' : `${alreadyRefundedQty} refunded`}
                              </span>
                            )}
                          </div>
                        </div>
                        {isSelected && !isFullyRefunded && (
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-muted-foreground">Qty:</span>
                            <input
                              type="number"
                              min={1}
                              max={maxRefundable}
                              value={selectedItem?.quantity || maxRefundable}
                              onChange={(e) => updateRefundQuantity(itemId, parseInt(e.target.value, 10) || 1, maxRefundable, unitPrice)}
                              className="w-16 px-2 py-1 text-sm bg-background border border-border rounded text-center"
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  )
                })}
              </div>

              {refundItems.size > 0 && (
                <div className="p-4 bg-primary/10 rounded-xl">
                  <div className="flex justify-between items-center">
                    <span className="font-semibold">Refund Total:</span>
                    <span className="text-2xl font-bold text-primary">Rs.{calculateRefundTotal().toFixed(2)}</span>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Step 3: Confirm */}
          {step === 'confirm' && (
            <div className="space-y-4">
              <div className="p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-xl flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-yellow-600 dark:text-yellow-400 text-sm">Confirm Refund</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    This action cannot be undone. Please verify all details before proceeding.
                  </p>
                </div>
              </div>

              {/* Refund Summary */}
              <div className="space-y-3">
                <div className="p-3 bg-muted/20 rounded-lg">
                  <p className="text-xs text-muted-foreground mb-2">Items to Refund:</p>
                  {Array.from(refundItems.values()).map((item, index) => (
                    <div key={index} className="flex justify-between text-sm py-1">
                      <span>{item.productName} × {item.quantity}</span>
                      <span className="font-bold">Rs.{item.refundAmount.toFixed(2)}</span>
                    </div>
                  ))}
                </div>

                {/* Reason */}
                <div>
                  <label className="block text-xs font-semibold text-muted-foreground mb-2">Refund Reason *</label>
                  <select
                    value={refundReason}
                    onChange={(e) => setRefundReason(e.target.value)}
                    className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm"
                  >
                    <option value="">Select a reason...</option>
                    {refundReasons.map(reason => (
                      <option key={reason} value={reason}>{reason}</option>
                    ))}
                  </select>
                </div>

                {/* Refund Method */}
                <div>
                  <label className="block text-xs font-semibold text-muted-foreground mb-2">Refund Method</label>
                  <div className="grid grid-cols-3 gap-2">
                    {refundMethods.map(method => (
                      <button
                        key={method.id}
                        onClick={() => setRefundMethod(method.id as any)}
                        className={`p-3 rounded-lg border-2 text-center transition-all ${
                          refundMethod === method.id
                            ? 'border-primary bg-primary/10'
                            : 'border-border/50 hover:border-primary/30'
                        }`}
                      >
                        <span className="text-xl">{method.icon}</span>
                        <p className="text-xs font-semibold mt-1">{method.label}</p>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Total */}
                <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-xl">
                  <div className="flex justify-between items-center">
                    <span className="font-semibold text-red-600 dark:text-red-400">Total Refund:</span>
                    <span className="text-2xl font-bold text-red-500">Rs.{calculateRefundTotal().toFixed(2)}</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Void confirm overlay */}
        {transactionToVoid && (
          <div className="absolute inset-0 z-10 bg-background/95 backdrop-blur-sm flex flex-col items-center justify-center p-4 rounded-xl">
            <div className="w-full max-w-sm space-y-4">
              <div className="flex items-center gap-2 text-red-600 dark:text-red-400">
                <Ban className="w-5 h-5" />
                <h4 className="font-semibold">Void transaction</h4>
              </div>
              <p className="text-sm text-muted-foreground">
                #{transactionToVoid.transactionNumber || transactionToVoid.id} — Rs.{Number(transactionToVoid.total).toFixed(2)}. Same-day only. Stock will be restored.
              </p>
              <div>
                <label className="block text-xs font-semibold text-muted-foreground mb-1">Reason (required)</label>
                <input
                  type="text"
                  value={voidReason}
                  onChange={(e) => setVoidReason(e.target.value)}
                  placeholder="e.g. Duplicate entry, wrong sale"
                  className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm"
                  autoFocus
                />
              </div>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => { setTransactionToVoid(null); setVoidReason('') }}
                  disabled={isVoiding}
                  className="flex-1 py-2 bg-muted hover:bg-muted/80 rounded-lg font-medium text-sm disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleVoidConfirm}
                  disabled={isVoiding || !voidReason.trim()}
                  className="flex-1 py-2 bg-red-500 text-white rounded-lg font-medium text-sm disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {isVoiding ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                  Void transaction
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="flex-shrink-0 mt-4 pt-4 border-t border-border/50 flex gap-3">
          {step === 'search' && (
            <button
              onClick={onClose}
              className="w-full py-3 bg-muted hover:bg-muted/80 rounded-xl font-semibold text-sm transition-colors"
            >
              Cancel
            </button>
          )}

          {step === 'select-items' && (
            <>
              <button
                onClick={() => setStep('search')}
                className="flex-1 py-3 bg-muted hover:bg-muted/80 rounded-xl font-semibold text-sm transition-colors"
              >
                Back
              </button>
              <button
                onClick={() => setStep('confirm')}
                disabled={refundItems.size === 0}
                className="flex-1 py-3 bg-primary text-primary-foreground rounded-xl font-semibold text-sm disabled:opacity-50 transition-colors"
              >
                Continue
              </button>
            </>
          )}

          {step === 'confirm' && (
            <>
              <button
                onClick={() => setStep('select-items')}
                disabled={isProcessing}
                className="flex-1 py-3 bg-muted hover:bg-muted/80 rounded-xl font-semibold text-sm transition-colors disabled:opacity-50"
              >
                Back
              </button>
              <button
                onClick={handleProcessRefund}
                disabled={isProcessing || !refundReason}
                className="flex-1 py-3 bg-red-500 text-white rounded-xl font-semibold text-sm disabled:opacity-50 flex items-center justify-center gap-2 transition-colors"
              >
                {isProcessing ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <RotateCcw className="w-4 h-4" />
                    Process Refund
                  </>
                )}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

export default RefundModal
